import React, { useCallback } from 'react'
import LazyHydrate from 'react-lazy-hydration'
import { HorizontalBannerScroller } from '@/molecules/HorizontalBannerScroller'
import { DiscoverItemInteraction, useContentInteraction } from '@/services/Analytics/ContentInteractions'
import { useRecommendedHeroBanners } from '@/services/RecommendedBannerService'
import { HeroBannerData } from '@/services/RecommendedBannerService/types'
import { getCloudinaryImageUrl } from '@/utils/Cloudinary'
import { RecommendedBanner } from '@/views/WatchLandingView/RecommendedBannerCarousel/RecommendedBanner/RecommendedBanner'
import { RecommendedBannerCarouselPlaceholder } from '@/views/WatchLandingView/RecommendedBannerCarousel/RecommendedBannerCarouselPlaceholder'
import { RecommendedBannerCarouselFull } from './RecommendedBannerCarouselFull'
import { RecommendedBannerCarouselFullPlaceholder } from './RecommendedBannerCarouselFullPlaceholder'

export const RecommendedBannerCarousel: React.FC = () => {
  const { heroBanners, loading } = useRecommendedHeroBanners()
  const { trackInteraction } = useContentInteraction()

  const createInteraction = useCallback((banner: HeroBannerData, itemIndex: number) => {
    const contentId = banner.id ?? ''
    const imageUrl =
      banner.project.title?.landscapeBackdrop?.cloudinaryPath ?? banner.project.discoveryPosterLandscapeCloudinaryPath

    if (!contentId || !imageUrl) return null

    return new DiscoverItemInteraction({
      id: contentId,
      __typename: banner?.__typename ?? 'HeroBanner',
      attribution: banner.attribution,
    })
      .withSurface('discover')
      .withPlacement('hero_carousel', 0, itemIndex, 'carousel')
      .withImageArtwork(getCloudinaryImageUrl({ path: imageUrl }), 'landscape_display')
  }, [])

  if (loading) {
    return (
      <LazyHydrate whenVisible>
        <div>
          {/* Desktop placeholder */}
          <div className="hidden lg:block" data-testid="banners-placeholder">
            <RecommendedBannerCarouselFullPlaceholder />
          </div>
          {/* Mobile placeholder */}
          <div className="lg:hidden" data-testid="banners-placeholder">
            <RecommendedBannerCarouselPlaceholder />
          </div>
        </div>
      </LazyHydrate>
    )
  }

  return (
    <LazyHydrate whenVisible>
      <div>
        {/* Desktop version */}
        <div className="hidden lg:block">
          <RecommendedBannerCarouselFull
            heroBanners={heroBanners}
            createInteraction={createInteraction}
            trackInteraction={trackInteraction}
          />
        </div>
        {/* Mobile version */}
        <div className="lg:hidden">
          <HorizontalBannerScroller
            className="recommended-banner-carousel group-recommended-banner"
            centerMode
            centerPadding="32px"
            chevronClassName="group-recommended-banner-hover:block group-recommended-banner-hover:opacity-100"
          >
            {heroBanners?.map((banner, index) => (
              <RecommendedBanner
                key={banner.id}
                banner={banner}
                itemIndex={index}
                createInteraction={createInteraction}
                trackInteraction={trackInteraction}
              />
            ))}
          </HorizontalBannerScroller>
        </div>
      </div>
    </LazyHydrate>
  )
}
